import { Fragment, type ReactNode } from 'react';

import type { StudentQuestion } from '@/api/ieltsMock';
import { splitSlots, type ServerLayout } from '@/lib/serverLayout';

/**
 * Задание по вёрстке с сервера (T3, виды Reading как на engnovate, 28.10.2026): заметки — рамка,
 * заголовок, подзаголовки, пункты и подпункты; резюме — заголовок и абзац с несколькими
 * пропусками. Поле на месте {{N}} рисует карточка (`gap`) — то же поле с номером, что у
 * задания без вёрстки. Разбор и проверка вёрстки — `lib/serverLayout.ts`.
 */
export function LayoutBody({ layout, gap }: { layout: ServerLayout; gap: (q: StudentQuestion) => ReactNode }) {
  const line = (text: string) =>
    splitSlots(text).map((p, i) => {
      if (p.kind === 'text') return <Fragment key={i}>{p.text}</Fragment>;
      const q = layout.bySlot.get(p.slot);
      return q ? <Fragment key={i}>{gap(q)}</Fragment> : null;
    });

  return (
    <div
      className={
        layout.frame
          ? 'rounded-lg border border-outline bg-surface/60 p-4 text-sm text-on-surface'
          : 'text-sm text-on-surface'
      }
    >
      {layout.title && <p className="mb-2 text-center font-semibold">{layout.title}</p>}
      <div className="space-y-1">
        {layout.blocks.map((b, i) => {
          if (b.type === 'heading') {
            return (
              <p key={i} className="pt-2 font-semibold">
                {b.text}
              </p>
            );
          }
          if (b.type === 'line') {
            return (
              <p key={i} className="leading-9">
                {line(b.text)}
              </p>
            );
          }
          return (
            <div key={i}>
              <div className="flex gap-2 leading-9">
                <span aria-hidden>•</span>
                <span className="min-w-0">{line(b.text)}</span>
              </div>
              {b.sub.map((s, j) => (
                <div key={j} className="pl-6 leading-9">
                  {line(s)}
                </div>
              ))}
            </div>
          );
        })}
      </div>
    </div>
  );
}
